export default {
  /** Used to persist array of recently viewed opportunities.
   * Checks if opp already in array and if so, updates its timestamp
   * If opp is not in recently viewed from state.recentlyViewedOpportunities, we add it with timestamp
   * Function also manages keeping only 10 most recently viewed and persists opportunities in sorted order by timestamp */
  persistRecentlyViewedOpp({ commit, state }, opp) {
    let timestamp = Date.now();
    opp.recentlyAccessedDate = timestamp;

    // first grab already persisted opportunity objects array
    let currentlySavedOpps = state.recentlyViewedOpportunities;

    // we already have some, check if same opp is recently viewed
    const index = currentlySavedOpps.findIndex((o) => o.id === opp.id);
    if (index !== -1) {
      // we found the index of the opp already added, update its timestamp and leave it
      currentlySavedOpps[index].recentlyAccessedDate = timestamp;
    } else {
      // New recent opp, add it to list with timestamp
      currentlySavedOpps.push(opp);
    }

    // sort them by date, most recent timestamp to be at index 0
    currentlySavedOpps.sort((a, b) => {
      let aUpdated = new Date(a.recentlyAccessedDate);
      let bUpdated = new Date(b.recentlyAccessedDate);
      return aUpdated.getTime() < bUpdated.getTime() ? 1 : -1;
    });

    // finally commit mutation to persist 10 most recent opportunities
    commit("setRecentlyViewedOpportunities", currentlySavedOpps.slice(0, 10));
  },

  /** Used to persist array of recently viewed brokerages.
   * Checks if brokerage already in array and if so, updates its timestamp
   * If brokerage is not in recently viewed from state.recentlyViewedBrokerages, we add it with timestamp
   * Function also manages keeping only 10 most recently viewed/impersonated and persists brokerages in sorted order by timestamp */
  persistRecentlyViewedBrokerage({ commit, state }, brokerage) {
    let timestamp = Date.now();
    brokerage.recentlyAccessedDate = timestamp;

    // first grab already persisted opportunity objects array
    let currentlySavedBrokerages = state.recentlyViewedBrokerages;

    // we already have some, check if same brokerage is recently viewed
    const index = currentlySavedBrokerages.findIndex(
      (b) => b.id === brokerage.id,
    );
    if (index !== -1) {
      // we found the index of the brokerage already added, update its timestamp and leave it
      currentlySavedBrokerages[index].recentlyAccessedDate = timestamp;
      //also just override status since it can change by hide/pause/activate actions
      currentlySavedBrokerages[index].status = brokerage.status;
    } else {
      // New recent brokerage, add it to list with timestamp
      currentlySavedBrokerages.push(brokerage);
    }

    // sort them by date, most recent timestamp to be at index 0
    currentlySavedBrokerages.sort((a, b) => {
      let aUpdated = new Date(a.recentlyAccessedDate);
      let bUpdated = new Date(b.recentlyAccessedDate);
      return aUpdated.getTime() < bUpdated.getTime() ? 1 : -1;
    });

    // finally commit mutation to persist 10 most recent brokerages
    commit(
      "setRecentlyViewedBrokerages",
      currentlySavedBrokerages.slice(0, 10),
    );
  },

  /** Used to persist array of recently viewed agents for broker admins.
   * Checks if agent already in array and if so, updates its timestamp
   * If agent is not in recently viewed from state.recentlyViewedAgents, we add it with timestamp
   * Function also manages keeping only 10 most recently viewed and persists agents in sorted order by timestamp */
  persistRecentlyViewedAgent({ commit, state }, agent) {
    let timestamp = Date.now();
    agent.recentlyAccessedDate = timestamp;

    // first grab already persisted agents objects array
    let currentlySavedAgents = state.recentlyViewedAgents;

    // we already have some, check if same agent is recently viewed
    const index = currentlySavedAgents.findIndex((a) => a.id === agent.id);
    if (index !== -1) {
      // we found the index of agent already added, update its timestamp and leave it
      currentlySavedAgents[index].recentlyAccessedDate = timestamp;
    } else {
      // New recent agent, add it to list with timestamp
      currentlySavedAgents.push(agent);
    }

    // sort them by date, most recent timestamp to be at index 0
    currentlySavedAgents.sort((a, b) => {
      let aUpdated = new Date(a.recentlyAccessedDate);
      let bUpdated = new Date(b.recentlyAccessedDate);
      return aUpdated.getTime() < bUpdated.getTime() ? 1 : -1;
    });

    // finally commit mutation to persist 10 most recent
    commit("setRecentlyViewedAgents", currentlySavedAgents.slice(0, 10));
  },

  /** Used to update already stored agents data for recently viewed widget
   *  timestamp and status in case its changed from agents list actions
   * @param commit
   * @param state
   * @param agentData - contains agentId and status fields
   */
  updateRecentlyViewedAgent({ commit, state }, agentData) {
    let timestamp = Date.now();

    // first grab already persisted agents objects array
    let currentlySavedAgents = state.recentlyViewedAgents;
    // we already have some, check if same agent is recently viewed
    const index = currentlySavedAgents.findIndex((a) => a.id === agentData.id);
    if (index !== -1) {
      // we found the index of agent already added, update its timestamp and status
      currentlySavedAgents[index].recentlyAccessedDate = timestamp;

      if (agentData.status) {
        currentlySavedAgents[index].status = agentData.status;
      }

      // sort them by date, most recent timestamp to be at index 0
      currentlySavedAgents.sort((a, b) => {
        let aUpdated = new Date(a.recentlyAccessedDate);
        let bUpdated = new Date(b.recentlyAccessedDate);
        return aUpdated.getTime() < bUpdated.getTime() ? 1 : -1;
      });

      // finally commit mutation to persist 10 most recent
      commit("setRecentlyViewedAgents", currentlySavedAgents.slice(0, 10));
    } else {
      // If business wants to manage actions and considering it "recent access" we will need to support adding new agent data
      // then we could probably remove this func and reuse one that persists all together
      console.log(
        "We did not find this agent, and agent actions are not supported.",
      );
    }
  },

  /** Used to persist array of recently viewed my agents for brokers accounts.
   * Checks if agent already in array and if so, updates its timestamp
   * If agent is not in recently viewed from state.recentlyViewedMyAgents, we add it with timestamp
   * Function also manages keeping only 10 most recently viewed and persists agents in sorted order by timestamp */
  persistRecentlyViewedMyAgent({ commit, state }, myAgent) {
    let timestamp = Date.now();
    myAgent.recentlyAccessedDate = timestamp;

    // first grab already persisted myAgent objects array
    let currentlySavedMyAgents = state.recentlyViewedMyAgents;

    // we already have some, check if same agent is recently viewed
    const index = currentlySavedMyAgents.findIndex(
      (ma) => ma.id === myAgent.id,
    );
    if (index !== -1) {
      // we found the index of the agent already added, update its timestamp and leave it
      currentlySavedMyAgents[index].recentlyAccessedDate = timestamp;
    } else {
      // New recent brokerage, add it to list with timestamp
      currentlySavedMyAgents.push(myAgent);
    }

    // sort them by date, most recent timestamp to be at index 0
    currentlySavedMyAgents.sort((a, b) => {
      let aUpdated = new Date(a.recentlyAccessedDate);
      let bUpdated = new Date(b.recentlyAccessedDate);
      return aUpdated.getTime() < bUpdated.getTime() ? 1 : -1;
    });
    // finally commit mutation to persist 10 most recent agents
    commit("setRecentlyViewedMyAgents", currentlySavedMyAgents.slice(0, 10));
  },

  /** Used to update already stored my agents data for recently viewed widget
   *  timestamp and status in case its changed from agents list actions
   * @param commit
   * @param state
   * @param myAgentData
   */
  updateRecentlyViewedMyAgent({ commit, state }, myAgentData) {
    let timestamp = Date.now();
    // first grab already persisted agents objects array
    let currentlySavedMyAgents = state.recentlyViewedMyAgents;
    // we already have some, check if same agent is recently viewed
    const index = currentlySavedMyAgents.findIndex(
      (a) => a.id === myAgentData.id,
    );
    if (index !== -1) {
      // we found the index of agent already added, update its timestamp and status
      currentlySavedMyAgents[index].recentlyAccessedDate = timestamp;

      if (myAgentData.status) {
        currentlySavedMyAgents[index].status = myAgentData.status;
      }

      // sort them by date, most recent timestamp to be at index 0
      currentlySavedMyAgents.sort((a, b) => {
        let aUpdated = new Date(a.recentlyAccessedDate);
        let bUpdated = new Date(b.recentlyAccessedDate);
        return aUpdated.getTime() < bUpdated.getTime() ? 1 : -1;
      });

      // finally commit mutation to persist 10 most recent
      commit("setRecentlyViewedMyAgents", currentlySavedMyAgents.slice(0, 10));
    } else {
      // If business wants to manage actions and considering it "recent access" we will need to support adding new agent data
      // then we could probably remove this func and reuse one that persists all together
      console.log(
        "We did not find this my agent, and my agent actions are not supported.",
      );
    }
  },
};
